import React, { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import { selectActiveTodoItem } from '../../../../redux/healthboard/selectors';
import { activeTodoActions } from '../../../../redux/healthboard/activeTodo';
import SequentialSurveyPopup from '../../../SequentialSurveyPopup';
import CompletedConsentPopup from '../CompletedConsentPopup';
import ProgramDetailsPopup from '../ProgramDetailsPopup';
import CarePlanDetailsPopup from '../../../CarePlanDetailsPopup';
import InvitationDetailsPopup from '../InvitationDetailsPopup';
import InsuranceToDoTask from '../InsuranceToDoTaskPopup';
import EmptyContent from '../../../common/EmptyState/Content';
import { useTodoRenderItem } from './useTodoRender';
import TaskDetailsPopup from '../TaskDetailsPopup';
import { TODO_TYPES } from '../../../../constants';
import { enhance } from './enhance';
import Loader from '../../../common/Loader';
import ElectronicConsentPopup from '../ElectronicConsentPopup';
import CompletedElectronicConsentPopup from '../CompletedElectronicConsentPopup';
import { isCompleted } from '../../utils';

function TodoList({
  list,
  loading,
  isUpcoming,
  targetUser,
  surveyProceeding,
  intl: { formatMessage },
}) {
  const activeItem = useSelector(selectActiveTodoItem);

  const dispatch = useDispatch();
  const setActiveItem = item => dispatch(activeTodoActions.setActiveItem(item));
  const { getRenderByType } = useTodoRenderItem(formatMessage, setActiveItem);

  const renderedList = useMemo(() => {
    return list.map(
      ({
        id,
        payload,
        type: todoType,
        status: todoStatus,
        expiredAt: expiredTime,
        completedAt: completedTime,
      }) => {
        const renderFn = getRenderByType(todoType);

        return (
          <Fragment key={id}>
            {renderFn({
              ...payload,
              todoType,
              todoStatus,
              itemId: id,
              expiredTime,
              completedTime,
            })}
          </Fragment>
        );
      },
    );
  }, [list]);

  const renderPopupByType = useMemo(() => {
    const type = get(activeItem, 'todoType');

    const isInsuranceToDoTask = get(activeItem, 'patientInsurance', null);

    const todoStatus = get(activeItem, 'todoStatus');

    const userId = get(targetUser, 'id');

    const isTaskCompleted = isCompleted(todoStatus);

    const opened = Boolean(get(activeItem, 'id', null));

    const closeModal = () => setActiveItem(null);

    switch (type) {
      case TODO_TYPES.INVITATION:
        return (
          <InvitationDetailsPopup
            key={get(activeItem, 'id')}
            invitationId={get(activeItem, 'id')}
            onClose={closeModal}
          />
        );
      case TODO_TYPES.SURVEY:
        return (
          <SequentialSurveyPopup
            key={get(activeItem, 'assignmentId')}
            assignmentId={get(activeItem, 'assignmentId')}
            onClose={closeModal}
          />
        );
      case TODO_TYPES.CARE_PLAN_TASK:
        return (
          <CarePlanDetailsPopup
            id={get(activeItem, 'id')}
            key={get(activeItem, 'id')}
            itemId={get(activeItem, 'itemId')}
            onClose={closeModal}
          />
        );
      case TODO_TYPES.CONSENT:
        return (
          <CompletedConsentPopup
            item={activeItem}
            opened={opened}
            onClose={closeModal}
            formatMessage={formatMessage}
            targetUser={targetUser}
          />
        );
      case TODO_TYPES.TASK: {
        const sharedProps = {
          key: get(activeItem, 'taskAssignmentId'),
          taskId: get(activeItem, 'taskAssignmentId'),
          task: activeItem,
          onClose: closeModal,
        };

        if (isInsuranceToDoTask) {
          return (
            <InsuranceToDoTask
              {...sharedProps}
              isCompleted={isTaskCompleted}
              userId={userId}
            />
          );
        }

        return <TaskDetailsPopup {...sharedProps} />;
      }
      case TODO_TYPES.TASK_CUSTOM: {
        const { id, name, description, ...task } = activeItem;
        return (
          <TaskDetailsPopup
            key={id}
            taskId={id}
            onClose={closeModal}
            task={{
              ...task,
              taskAssignmentId: id,
              todoTask: { name, description },
            }}
          />
        );
      }
      case TODO_TYPES.PROGRAM:
        return (
          <ProgramDetailsPopup
            item={activeItem}
            targetUser={targetUser}
            key={get(activeItem, 'id')}
            formatMessage={formatMessage}
            onClose={closeModal}
            opened={opened}
          />
        );
      case TODO_TYPES.CONSENT_USER_ASSIGNMENT: {
        return isTaskCompleted ? (
          <CompletedElectronicConsentPopup
            formatMessage={formatMessage}
            item={activeItem}
            onClose={closeModal}
            opened={opened}
            targetUser={targetUser}
          />
        ) : (
          <ElectronicConsentPopup
            item={activeItem}
            key={get(activeItem, 'id')}
            onClose={closeModal}
            opened={opened}
          />
        );
      }

      default:
        return null;
    }
  }, [activeItem]);

  const translationId = useMemo(() => {
    return isUpcoming
      ? 'healthboard.title.todoEmpty'
      : 'healthboard.title.todoCompleted';
  }, [isUpcoming]);

  return (
    <div className={cn('pt3', { disabled: surveyProceeding })}>
      <If condition={loading}>
        <Loader />
      </If>
      <If condition={!loading && list.length}>
        {renderedList}
        {renderPopupByType}
      </If>
      <If condition={!loading && !list.length}>
        <EmptyContent
          icon="no-surveys"
          text={formatMessage({ id: translationId })}
          className="absolute-center g-color--primary"
        />
      </If>
    </div>
  );
}

TodoList.propTypes = {
  surveyProceeding: PropTypes.bool,
  loading: PropTypes.bool,
  list: PropTypes.array,
  intl: PropTypes.object,
  targetUser: PropTypes.object,
  isUpcoming: PropTypes.bool,
};

export default enhance(TodoList);



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/TodoList/index.jsx